import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  AlarmClock, CalendarClock, CalendarPlus, CalendarX, Check, ClipboardList, FileText, Mail, MessageSquare, Pencil, Phone, PhoneCall, Plus, StickyNote, UserCheck, UserX, Video, X, ArrowRightLeft, Info,
  ArrowDownLeft, ArrowUpRight, Copy, Lock,
} from "lucide-react";
import { api, formatDateTime, formatPhone } from "../../api";
import { useApi } from "../../hooks";
import { useAuth } from "../../auth";
import { STAGES, callStatusLabel, contactName, easternText, moneyField, parseMoney, shortWhen, wasRescheduled, whenLabel, type StageId } from "../../crm";
import { attributionRows, cameFrom, shortUrl, type Attribution as AttributionValue, type VisitJourney } from "../../crmAttribution";
import MoneyInput from "./MoneyInput";
import CloseDialog from "./CloseDialog";
import { ErrorState, LoadingState, monogram } from "../Ui";
import BookingPicker, { BookedPanel, type BookedResult } from "./BookingPicker";
import useNotice from "./useNotice";
import ContactDrips from "./ContactDrips";
import type { Activity, BookingRow, Calendar, ContactDetail, Deal, Person } from "./types";

const KIND_ICON: Record<Activity["kind"], ReactNode> = {
  note: <StickyNote aria-hidden="true" />,
  call: <PhoneCall aria-hidden="true" />,
  email: <Mail aria-hidden="true" />,
  sms: <MessageSquare aria-hidden="true" />,
  form: <ClipboardList aria-hidden="true" />,
  booking: <CalendarClock aria-hidden="true" />,
  stage: <ArrowRightLeft aria-hidden="true" />,
  task: <Check aria-hidden="true" />,
  system: <Info aria-hidden="true" />,
};
const LOG_KINDS = [
  { id: "note", label: "Note" },
  { id: "call", label: "Call" },
  { id: "email", label: "Email" },
  { id: "sms", label: "Text" },
] as const;

/** A right-hand panel over the page (a modal dialog, so focus and Escape behave). */
export function Drawer({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const el = ref.current;
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    el?.showModal();
    // Focus the name, not the close button (a ring on X the moment it opens).
    el?.querySelector<HTMLElement>(".crm-drawer-title h2")?.focus({ preventScroll: true });
    return () => { document.body.style.overflow = overflow; el?.close(); previous?.focus(); };
  }, []);
  return (
    <dialog
      ref={ref}
      className="crm-drawer"
      aria-label={title}
      onCancel={(e) => { e.preventDefault(); onClose(); }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="crm-drawer-body">{children}</div>
    </dialog>
  );
}

/**
 * One contact: who they are, their deal (stage, amount, owner), a box to log a
 * note or call, the timeline (form answers, bookings, stage moves), and their calls.
 */
export default function ContactDrawer({ id, onClose, onChanged }: { id: number; onClose: () => void; onChanged: () => void }) {
  const detail = useApi<ContactDetail>(`/api/crm/contacts/${id}`);
  const people = useApi<Person[]>("/api/tasks/assignees");
  const { user } = useAuth();
  const canWrite = user?.role === "superadmin" || user?.role === "exec";
  const [editing, setEditing] = useState(false);
  const [params, setParams] = useSearchParams();
  const [panel, setPanel] = useState<"" | "book" | "follow">(() => (params.get("panel") === "book" ? "book" : ""));
  /** The call being moved (Reschedule), by booking id. */
  const [moving, setMoving] = useState<number | null>(() => (params.get("panel") === "book" && Number(params.get("booking"))) || null);
  // The URL asked for a panel once (Reschedule); later visits open the plain drawer.
  useEffect(() => {
    if (params.get("panel") || params.get("booking")) setParams((cur) => { const n = new URLSearchParams(cur); n.delete("panel"); n.delete("booking"); return n; }, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [notice, setNotice] = useNotice();
  const openPanel = (next: "" | "book" | "follow", movingId: number | null = null) => { setPanel(next); setMoving(next === "book" ? movingId : null); };
  // Reschedule is pressed down in Calls, but the picker opens up top: bring it into view.
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!panel) return;
    const el = panelRef.current;
    const body = el?.closest(".crm-drawer-body");
    if (!el || !body) return;
    const top = el.getBoundingClientRect().top - body.getBoundingClientRect().top;
    // Only when it is out of sight (above the sticky header or low on the screen).
    if (top > 72 && top < body.clientHeight - 160) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ block: "start", behavior: reduce ? "auto" : "smooth" });
  }, [panel, moving]);
  const d = detail.data;
  const refresh = () => { detail.reload(); onChanged(); };
  const name = d ? contactName(d.contact) : "Contact";

  return (
    <Drawer title={name} onClose={onClose}>
      <header className="crm-drawer-head">
        {d && <span className="crm-avatar" aria-hidden="true">{monogram(name)}</span>}
        <div className="crm-drawer-title">
          <h2 tabIndex={-1}>{name}</h2>
          {d && (d.contact.company || d.contact.title) && <p>{[d.contact.title, d.contact.company].filter(Boolean).join(" at ")}</p>}
        </div>
        <button type="button" className="icon-button" onClick={onClose} aria-label="Close contact"><X aria-hidden="true" /></button>
      </header>
      {detail.loading && !d ? <LoadingState label="Loading contact" />
        : detail.error || !d ? <ErrorState message={detail.error || "This contact could not load."} onRetry={detail.reload} />
        : (
          <>
            <div className="crm-quick">
              {d.contact.email && <a className="button secondary small" href={`mailto:${d.contact.email}`}><Mail aria-hidden="true" />Email</a>}
              {d.contact.phone && <a className="button secondary small" href={`tel:${d.contact.phone}`}><Phone aria-hidden="true" />Call</a>}
              {d.contact.phone && <a className="button secondary small" href={`sms:${d.contact.phone}`}><MessageSquare aria-hidden="true" />Text</a>}
              {canWrite && <button type="button" className={`button secondary small${panel === "book" ? " is-on" : ""}`} aria-expanded={panel === "book"} onClick={() => openPanel(panel === "book" ? "" : "book")}><CalendarPlus aria-hidden="true" />Book a call</button>}
              {canWrite && <button type="button" className={`button secondary small${panel === "follow" ? " is-on" : ""}`} aria-expanded={panel === "follow"} onClick={() => openPanel(panel === "follow" ? "" : "follow")}><AlarmClock aria-hidden="true" />Follow up</button>}
              {canWrite && <button type="button" className={`button secondary small${editing ? " is-on" : ""}`} aria-expanded={editing} onClick={() => setEditing((v) => !v)}><Pencil aria-hidden="true" />Edit</button>}
            </div>
            {notice && <p className="inline-notice" role="status">{notice}</p>}
            {panel && <div ref={panelRef} className="crm-panel-anchor" aria-hidden="true" />}
            {panel === "book" && (
              <StaffBooking
                key={moving || "new"}
                contactId={d.contact.id}
                upcoming={d.bookings.filter((b) => b.status === "booked" && new Date(b.starts_at).getTime() > Date.now())}
                moving={d.bookings.find((b) => b.id === moving && b.status === "booked") || null}
                onBooked={refresh}
                onDone={(msg) => { openPanel(""); setNotice(msg); }}
              />
            )}
            {panel === "follow" && <FollowUp contactId={d.contact.id} ownerId={d.contact.owner_id} people={people.data || []} onDone={(msg) => { openPanel(""); setNotice(msg); refresh(); }} />}
            {editing ? (
              <ContactEdit detail={d} people={people.data || []} canArchive={user?.role === "superadmin"} onArchived={() => { onChanged(); onClose(); }} onDone={(msg) => { setEditing(false); if (msg) setNotice(msg); refresh(); }} />
            ) : (
              <dl className="crm-facts">
                <div><dt>Email</dt><dd>{d.contact.email ? <a href={`mailto:${d.contact.email}`}>{d.contact.email}</a> : "None"}</dd></div>
                <div><dt>Phone</dt><dd>{d.contact.phone ? <a href={`tel:${d.contact.phone}`}>{formatPhone(d.contact.phone)}</a> : "None"}</dd></div>
                <div><dt>Owner</dt><dd>{d.contact.owner_name || "Unassigned"}</dd></div>
                <div><dt>Source</dt><dd>{sourceText(d.contact.source, d.contact.source_detail)}</dd></div>
                {d.contact.website && <div><dt>Website</dt><dd><a href={d.contact.website.startsWith("http") ? d.contact.website : `https://${d.contact.website}`} target="_blank" rel="noreferrer">{d.contact.website.replace(/^https?:\/\//, "")}</a></dd></div>}
                <div><dt>Added</dt><dd>{formatDateTime(d.contact.created_at)}</dd></div>
                {d.contact.notes && <div className="wide"><dt>Notes</dt><dd className="crm-pre">{d.contact.notes}</dd></div>}
              </dl>
            )}

            {/* The contact keeps their first tracked visit; one who never had one shows their latest form's. */}
            <Attribution data={attributionRows(d.contact.utm).length ? d.contact.utm : d.submissions.find((s) => attributionRows(s.utm).length)?.utm || {}} booking={d.bookings.find(b => b.status !== 'cancelled' && b.utm && attributionRows(b.utm).length)?.utm} />

            <section className="crm-section" aria-label="Deals">
              <h3>Deal{d.deals.length > 1 ? "s" : ""}</h3>
              {d.deals.length ? d.deals.map((deal) => (
                <DealEditor key={deal.id} deal={deal} people={people.data || []} canWrite={canWrite} isSuperadmin={user?.role === "superadmin"} onChanged={(msg) => { if (msg) setNotice(msg); refresh(); }} />
              )) : <p className="crm-empty">No deal yet.</p>}
              {canWrite && !d.deals.some((x) => STAGES.find((s) => s.id === x.stage)?.open) && <AddDeal contactId={d.contact.id} onDone={(msg) => { setNotice(msg); refresh(); }} />}
            </section>

            {d.bookings.length > 0 && (
              <section className="crm-section" aria-label="Calls">
                <h3>Calls</h3>
                <ul className="crm-calls">
                  {d.bookings.map((b) => (
                    <li key={b.id}>
                      <span className="crm-call-when">{shortWhen(b.starts_at)}<small>{b.calendar_name}</small></span>
                      {/* A call moved to a new time reads "Moved", as Bookings leaves it off Cancelled. */}
                      <span className={`crm-call-status s-${wasRescheduled(b) ? "moved" : b.status}`}>{wasRescheduled(b) ? "Moved" : callStatusLabel(b.status, b.ends_at)}</span>
                      {b.meeting_url && b.status === "booked" && <a className="icon-button" href={b.meeting_url} target="_blank" rel="noreferrer" aria-label="Open the video call"><Video aria-hidden="true" /></a>}
                      {canWrite && <BookingActions id={b.id} status={b.status} past={new Date(b.starts_at).getTime() < Date.now()} onDone={refresh} onReschedule={() => openPanel("book", b.id)} />}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {d.contact.email && <Emails contactId={d.contact.id} name={d.contact.first_name || contactName(d.contact)} userId={user?.id ?? null} canWrite={canWrite} onTask={refresh} />}
            {d.contact.email && <ContactDrips contactId={d.contact.id} canWrite={canWrite} />}

            {canWrite && <LogBox contactId={d.contact.id} dealId={d.deals.find((x) => STAGES.find((s) => s.id === x.stage)?.open)?.id || d.deals[0]?.id || null} onSaved={refresh} />}

            <section className="crm-section" aria-label="Timeline">
              <h3>Timeline</h3>
              {d.activities.length ? (
                <ol className="crm-timeline">
                  {d.activities.map((a) => (
                    <li key={a.id} className={`k-${a.kind}`}>
                      <span className="crm-tl-icon">{KIND_ICON[a.kind]}</span>
                      <div className="crm-tl-body">
                        {/* Booking lines the server wrote say "10:00 AM EDT"; the CRM says "10:00 AM ET". */}
                        <p className={a.kind === "note" || a.kind === "call" || a.kind === "email" || a.kind === "sms" ? "crm-pre" : undefined}>{a.kind === "call" ? `Call: ${a.body}` : a.kind === "email" ? `Email: ${a.body}` : a.kind === "sms" ? `Text: ${a.body}` : easternText(a.body)}</p>
                        {a.data?.answers && a.data.answers.length > 0 && (
                          <dl className="crm-answers">{a.data.answers.map((x, i) => <div key={i}><dt>{x.question}</dt><dd>{x.answer}</dd></div>)}</dl>
                        )}
                        {a.data?.dq_reasons && a.data.dq_reasons.length > 0 && <p className="crm-dq">Not a fit: {a.data.dq_reasons.join("; ")}</p>}
                        <small>{formatDateTime(a.created_at)}{a.user_name ? `, ${a.user_name}` : ""}</small>
                      </div>
                    </li>
                  ))}
                </ol>
              ) : <p className="crm-empty">Nothing yet.</p>}
            </section>
          </>
        )}
    </Drawer>
  );
}

const sourceText = (source: string, detail: string) =>
  source === "form" ? `Form${detail ? `: ${detail}` : ""}` : source === "form_left" ? `Left a form without sending it${detail ? `: ${detail}` : ""}`
    : source === "booking" ? `Booking page${detail ? `: ${detail}` : ""}`
      : source === "phone_call" ? `Phone call${detail ? `: ${detail}` : ""}` : source === "manual" ? "Added by hand" : source;

/**
 * Where they came from: UTM tags, ad click ids, the page they landed on and the site
 * that sent them (crmAttribution.ts). Only rows with a value; long ids are cut short
 * with the full value on hover and a copy button.
 */
function Attribution({ data, booking }: { data: VisitJourney; booking?: VisitJourney }) {
  const first = data.first_touch || data;
  const latest = data.last_touch || data;
  const touches: { label: string; value: AttributionValue }[] = [{ label: 'First touch', value: first }, { label: 'Latest touch', value: latest }];
  if (booking) touches.push({ label: 'Latest booking', value: booking.last_touch || booking });
  if (!touches.some(t => attributionRows(t.value).length)) return null;
  return (
    <section className="crm-section" aria-label="Attribution">
      <h3>Attribution</h3>
      {touches.filter(t => attributionRows(t.value).length).map(touch => (
      <details key={touch.label} className="crm-touch">
      <summary>{touch.label}: {cameFrom(touch.value) || 'Tracked visit'}</summary>
      <dl className="crm-facts crm-attr">
        {attributionRows(touch.value).map((r) => (
          <div key={r.key} className={r.kind === "url" ? "wide" : undefined}>
            <dt>{r.label}</dt>
            <dd>
              {r.kind === "url" ? <a className="crm-attr-val" href={r.value} target="_blank" rel="noreferrer" title={r.value}>{shortUrl(r.value)}</a>
                : r.kind === "id" ? <span className="crm-attr-id"><span className="crm-attr-val" title={r.value}>{r.value}</span><CopyValue value={r.value} label={r.label} /></span>
                : r.value}
            </dd>
          </div>
        ))}
      </dl>
      </details>
      ))}
    </section>
  );
}

function CopyValue({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try { await navigator.clipboard.writeText(value); setCopied(true); window.setTimeout(() => setCopied(false), 1600); } catch { /* the value is in the tooltip */ }
  }
  return (
    <button type="button" className="icon-button crm-copy-value" aria-label={copied ? "Copied" : `Copy ${label}`} title={copied ? "Copied" : `Copy ${label}`} onClick={() => void copy()}>
      {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
    </button>
  );
}

type EmailRow = { id: string; threadId: string; direction: "in" | "out"; from: string; subject: string; snippet: string; date: string | null; link: string };
type EmailsData = { connected: boolean; account?: string | null; emails?: EmailRow[] };

/** "2:15 PM ET" today, "Oct 5" this year, "Oct 5, 2025" before: on the agency's clock. */
function mailWhen(iso: string | null) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const day = (x: Date) => new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit" }).format(x);
  const now = new Date();
  if (day(d) === day(now)) return `${new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" }).format(d).replace(/\s+/g, " ")} ET`;
  const sameYear = day(d).slice(0, 4) === day(now).slice(0, 4);
  return new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", ...(sameYear ? {} : { year: "numeric" }) }).format(d);
}

/**
 * Your emails with this contact, read from your own Gmail each time the drawer opens
 * (GET /api/crm/contacts/:id/emails). Private by design: the server only ever reads
 * the signed-in person's account, so a teammate opening this contact sees their own.
 */
function Emails({ contactId, name, userId, canWrite, onTask }: { contactId: number; name: string; userId: number | null; canWrite: boolean; onTask: () => void }) {
  const mail = useApi<EmailsData>(`/api/crm/contacts/${contactId}/emails`);
  const [made, setMade] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const emails = mail.data?.emails || [];
  async function makeTask(m: EmailRow) {
    setBusy(m.id); setError("");
    try {
      await api(`/api/crm/contacts/${contactId}/follow-up`, { method: "POST", body: JSON.stringify({ due_date: today(), assigned_to: userId, title: m.subject || `Reply to ${name}` }) });
      setMade((x) => ({ ...x, [m.id]: true }));
      onTask();
    } catch (e) { setError(e instanceof Error ? e.message : "The task was not added."); }
    finally { setBusy(""); }
  }
  const account = mail.data?.account;
  return (
    <section className="crm-section" aria-label="Emails">
      <div className="crm-section-head">
        <h3>Emails</h3>
        {mail.data?.connected && (
          <span className="crm-private" title={`Only you see these. They come from your Gmail${account ? `, ${account}` : ""}.`}>
            <Lock aria-hidden="true" /><span className="sr-only">Only you see these emails</span>
          </span>
        )}
      </div>
      {mail.loading && !mail.data ? <p className="crm-empty">Loading emails…</p>
        : mail.error ? <p className="crm-empty">{mail.error} <button type="button" className="text-button crm-mail-retry" onClick={mail.reload}>Try again</button></p>
        : !mail.data?.connected ? <p className="crm-empty"><Link to="/settings#my-google">Connect your Gmail in Settings</Link> to see your emails with {name} here.</p>
        : !emails.length ? <p className="crm-empty">No emails with {name} in your Gmail.</p>
        : (
          <ul className="crm-mails">
            {emails.map((m) => (
              <li key={m.id}>
                <span className="crm-tl-icon" title={`From ${m.from}`}>
                  {m.direction === "in" ? <ArrowDownLeft aria-hidden="true" /> : <ArrowUpRight aria-hidden="true" />}
                  <span className="sr-only">{m.direction === "in" ? "Received" : "Sent"}</span>
                </span>
                <a className="crm-mail-main" href={m.link.startsWith("https://mail.google.com/") ? m.link : undefined} target="_blank" rel="noreferrer">
                  <span className="crm-mail-top">
                    <strong>{m.subject || "(no subject)"}</strong>
                    {m.date && <time dateTime={m.date} title={formatDateTime(m.date)}>{mailWhen(m.date)}</time>}
                  </span>
                  {m.snippet && <span className="crm-mail-snippet">{m.snippet}</span>}
                </a>
                {canWrite && (made[m.id]
                  ? <span className="crm-mail-task is-done"><Check aria-hidden="true" />Task added</span>
                  : <button type="button" className="text-button crm-mail-task" disabled={busy === m.id} onClick={() => void makeTask(m)}>{busy === m.id ? "Adding…" : "Make a task"}</button>)}
              </li>
            ))}
          </ul>
        )}
      {error && <p className="crm-error" role="alert">{error}</p>}
    </section>
  );
}

/** Mark a call as showed or no-show, or cancel it. */
export function BookingActions({ id, status, past, onDone, onReschedule }: { id: number; status: string; past: boolean; onDone: () => void; onReschedule?: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function run(path: string, body: unknown, method = "PATCH") {
    setBusy(true); setError("");
    try { await api(path, { method, body: JSON.stringify(body) }); onDone(); }
    catch (e) { setError(e instanceof Error ? e.message : "That did not save."); }
    finally { setBusy(false); }
  }
  if (status === "cancelled") return null;
  return (
    <span className="crm-call-actions">
      {past || status !== "booked" ? (
        <>
          <button type="button" className={`chip chip-sm${status === "showed" ? " on" : ""}`} disabled={busy} onClick={() => void run(`/api/crm/bookings/${id}`, { status: status === "showed" ? "booked" : "showed" })}><UserCheck aria-hidden="true" />Showed</button>
          <button type="button" className={`chip chip-sm${status === "no_show" ? " on" : ""}`} disabled={busy} onClick={() => void run(`/api/crm/bookings/${id}`, { status: status === "no_show" ? "booked" : "no_show" })}><UserX aria-hidden="true" />No-show</button>
        </>
      ) : (
        <>
          {onReschedule && <button type="button" className="chip chip-sm" disabled={busy} onClick={onReschedule}><CalendarClock aria-hidden="true" />Reschedule</button>}
          <button type="button" className="chip chip-sm" disabled={busy} onClick={() => { if (window.confirm("Cancel this call? If it is on Google Calendar, Google emails the guest. Otherwise, let them know yourself.")) void run(`/api/crm/bookings/${id}/cancel`, {}, "POST"); }}><CalendarX aria-hidden="true" />Cancel</button>
        </>
      )}
      {error && <span className="crm-error" role="alert">{error}</span>}
    </span>
  );
}

function DealEditor({ deal, people, canWrite, isSuperadmin, onChanged }: { deal: Deal; people: Person[]; canWrite: boolean; isSuperadmin: boolean; onChanged: (msg?: string) => void }) {
  const [amount, setAmount] = useState(moneyField(deal.value_cents));
  const [amountSaved, setAmountSaved] = useState(false);
  // A reload (another tab, the board) brings a new amount; show it unless someone is typing.
  useEffect(() => {
    if (!document.activeElement?.closest(".crm-deal .money-field")) setAmount(moneyField(deal.value_cents));
  }, [deal.value_cents]);
  const [closing, setClosing] = useState<StageId | null>(null);
  const changeStage = (stage: StageId) => {
    // The same questions the board asks: Won wants the contract amount, Lost wants a reason.
    if ((stage === "won" && !deal.value_cents) || stage === "lost") setClosing(stage);
    else void patch({ stage });
  };
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function patch(body: Record<string, unknown>, msg?: string) {
    setBusy(true); setError("");
    try { await api(`/api/crm/deals/${deal.id}`, { method: "PATCH", body: JSON.stringify(body) }); onChanged(msg); }
    catch (e) { setError(e instanceof Error ? e.message : "That did not save."); }
    finally { setBusy(false); }
  }
  async function makeClient() {
    setBusy(true); setError("");
    try { await api<{ client_id: number }>(`/api/crm/deals/${deal.id}/client`, { method: "POST" }); onChanged(`${deal.title} is now a NOLA client.`); }
    catch (e) { setError(e instanceof Error ? e.message : "The client could not be created."); }
    finally { setBusy(false); }
  }
  const saveAmount = async () => {
    const cents = parseMoney(amount);
    if (cents === null) { setError("Enter the amount in dollars, like 12,500 or 12.5k."); return; }
    setAmount(moneyField(cents));
    if (cents === Number(deal.value_cents || 0)) return;
    setBusy(true); setError("");
    try {
      await api(`/api/crm/deals/${deal.id}`, { method: "PATCH", body: JSON.stringify({ value_cents: cents }) });
      setAmountSaved(true);
      window.setTimeout(() => setAmountSaved(false), 1600);
      onChanged();
    } catch (e) { setError(e instanceof Error ? e.message : "That did not save."); }
    finally { setBusy(false); }
  };
  return (
    <div className="crm-deal">
      <div className="crm-deal-top">
        <strong>{deal.title}</strong>
        {deal.form_name && <small>From {deal.form_name}</small>}
      </div>
      <div className="crm-deal-grid">
        <label>Stage
          <select value={deal.stage} disabled={!canWrite || busy} onChange={(e) => changeStage(e.target.value as StageId)}>
            {STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </label>
        <label>Contract amount
          <MoneyInput value={amount} disabled={!canWrite} saved={amountSaved} onChange={(v) => { setAmount(v); setError(""); }} onCommit={() => void saveAmount()} onRevert={() => { setAmount(moneyField(deal.value_cents)); setError(""); }} />
        </label>
        <label>Owner
          <select value={deal.owner_id ?? ""} disabled={!canWrite || busy} onChange={(e) => void patch({ owner_id: e.target.value || null })}>
            <option value="">Unassigned</option>
            {people.map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
          </select>
        </label>
      </div>
      {deal.stage === "lost" && deal.lost_reason && <p className="crm-dq">Lost: {deal.lost_reason}</p>}
      {deal.stage === "won" && (
        deal.client_id
          ? <p className="crm-deal-client"><Link to={`/clients/${deal.client_id}`}>Open {deal.client_name || "the client"} in NOLA</Link></p>
          : isSuperadmin && <button type="button" className="button secondary small" disabled={busy} onClick={() => void makeClient()}><FileText aria-hidden="true" />Make NOLA client</button>
      )}
      {error && <p className="crm-error" role="alert">{error}</p>}
      {closing && (
        <CloseDialog
          pending={{ deal, stage: closing }}
          onCancel={() => setClosing(null)}
          onConfirm={(extra) => { const stage = closing; setClosing(null); if (typeof extra.value_cents === "number") setAmount(moneyField(extra.value_cents)); void patch({ stage, ...extra }); }}
        />
      )}
    </div>
  );
}

function LogBox({ contactId, dealId, onSaved }: { contactId: number; dealId: number | null; onSaved: () => void }) {
  const [kind, setKind] = useState<(typeof LOG_KINDS)[number]["id"]>("note");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function save(e: FormEvent) {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true); setError("");
    try {
      await api(`/api/crm/contacts/${contactId}/activities`, { method: "POST", body: JSON.stringify({ kind, body, deal_id: dealId }) });
      setBody("");
      onSaved();
    } catch (err) { setError(err instanceof Error ? err.message : "That did not save."); }
    finally { setBusy(false); }
  }
  return (
    <form className="crm-log" onSubmit={save}>
      <div className="segmented-control" role="tablist" aria-label="What to log">
        {LOG_KINDS.map((k) => <button key={k.id} type="button" role="tab" aria-selected={kind === k.id} className={kind === k.id ? "active" : ""} onClick={() => setKind(k.id)}>{k.label}</button>)}
      </div>
      <textarea
        rows={3}
        value={body}
        maxLength={4000}
        onChange={(e) => setBody(e.target.value)}
        placeholder={kind === "note" ? "Add a note" : kind === "call" ? "How did the call go?" : kind === "email" ? "What did you send or hear back?" : "What did you text?"}
        aria-label="Log entry"
      />
      <div className="crm-log-foot">
        {error && <span className="crm-error" role="alert">{error}</span>}
        <button className="button small" type="submit" disabled={busy || !body.trim()}>{busy ? "Saving…" : kind === "note" ? "Add note" : "Log it"}</button>
      </div>
    </form>
  );
}

function ContactEdit({ detail, people, canArchive, onArchived, onDone }: { detail: ContactDetail; people: Person[]; canArchive: boolean; onArchived: () => void; onDone: (msg?: string) => void }) {
  const c = detail.contact;
  // The phone shows the way it reads everywhere else, "(515) 555-0188", not raw digits.
  const shownPhone = c.phone ? formatPhone(c.phone) : "";
  const [f, setF] = useState({ first_name: c.first_name, last_name: c.last_name, email: c.email, phone: shownPhone, company: c.company, title: c.title, website: c.website, notes: c.notes, owner_id: c.owner_id ? String(c.owner_id) : "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  async function archive() {
    if (!window.confirm(`Archive ${contactName(c)}? They leave the CRM lists; a new form fill with the same email brings them back.`)) return;
    setBusy(true); setError("");
    try { await api(`/api/crm/contacts/${c.id}`, { method: "DELETE" }); onArchived(); }
    catch (err) { setError(err instanceof Error ? err.message : "The contact was not archived."); setBusy(false); }
  }
  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api(`/api/crm/contacts/${c.id}`, { method: "PATCH", body: JSON.stringify({ ...f, phone: f.phone === shownPhone ? c.phone : f.phone, owner_id: f.owner_id || null }) });
      onDone("Contact saved.");
    } catch (err) { setError(err instanceof Error ? err.message : "That did not save."); }
    finally { setBusy(false); }
  }
  return (
    <form className="crm-edit" onSubmit={save}>
      <div className="crm-edit-grid">
        <label>First name<input value={f.first_name} onChange={set("first_name")} maxLength={80} /></label>
        <label>Last name<input value={f.last_name} onChange={set("last_name")} maxLength={80} /></label>
        <label>Email<input type="email" value={f.email} onChange={set("email")} maxLength={200} /></label>
        <label>Phone<input type="tel" value={f.phone} onChange={set("phone")} maxLength={40} /></label>
        <label>Company<input value={f.company} onChange={set("company")} maxLength={160} /></label>
        <label>Job title<input value={f.title} onChange={set("title")} maxLength={120} /></label>
        <label>Website<input value={f.website} onChange={set("website")} maxLength={300} /></label>
        <label>Owner
          <select value={f.owner_id} onChange={set("owner_id")}>
            <option value="">Unassigned</option>
            {people.map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
          </select>
        </label>
        <label className="wide">Notes<textarea rows={3} value={f.notes} onChange={set("notes")} maxLength={5000} /></label>
      </div>
      {error && <p className="crm-error" role="alert">{error}</p>}
      <div className="crm-edit-actions">
        {canArchive && <button type="button" className="button secondary small crm-danger" disabled={busy} onClick={() => void archive()}>Archive contact</button>}
        <span className="crm-spacer" />
        <button type="button" className="button secondary small" onClick={() => onDone()}>Cancel</button>
        <button type="submit" className="button small" disabled={busy}>{busy ? "Saving…" : "Save contact"}</button>
      </div>
    </form>
  );
}

/** A new deal for a contact with nothing open (a returning client, a second property). */
function AddDeal({ contactId, onDone }: { contactId: number; onDone: (msg: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function add() {
    setBusy(true); setError("");
    try {
      const d = await api<{ title: string }>("/api/crm/deals", { method: "POST", body: JSON.stringify({ contact_id: contactId, stage: "new_lead" }) });
      onDone(`${d.title} added to New lead.`);
    } catch (e) { setError(e instanceof Error ? e.message : "The deal could not be added."); }
    finally { setBusy(false); }
  }
  return <div><button type="button" className="button secondary small" disabled={busy} onClick={() => void add()}><Plus aria-hidden="true" />Add deal</button>{error && <p className="crm-error" role="alert">{error}</p>}</div>;
}

/**
 * Book a call for this contact on a calendar that is taking bookings. With `moving`
 * (Reschedule) the new time replaces that call: the old one is cancelled once the
 * new one is booked, so a reschedule never leaves two calls on the books.
 */
function StaffBooking({ contactId, upcoming, moving: movingNow, onBooked, onDone }: { contactId: number; upcoming: BookingRow[]; moving: BookingRow | null; onBooked: () => void; onDone: (msg: string) => void }) {
  const calendars = useApi<Calendar[]>("/api/crm/calendars");
  const open = (calendars.data || []).filter((c) => c.active);
  const [calId, setCalId] = useState<number | null>(null);
  const [booked, setBooked] = useState<BookedResult | null>(null);
  // The call being moved, as it was when the panel opened (the drawer refreshes once the move is made).
  const [moving] = useState(movingNow);
  const preferred = moving && open.some((c) => c.id === moving.calendar_id) ? moving.calendar_id! : open[0]?.id;
  const cal = open.find((c) => c.id === (calId ?? preferred));
  const others = upcoming.filter((b) => b.id !== moving?.id);
  const done = (result: BookedResult) => `Call ${moving ? "moved to" : "booked for"} ${whenLabel(result.starts_at, "America/New_York")} ET.`;
  if (calendars.loading && !calendars.data) return <LoadingState label="Loading calendars" />;
  if (!open.length) return <div className="crm-panel"><p className="crm-empty">No calendar is taking bookings. Add or turn one on under <Link to="/crm/bookings">Bookings</Link>.</p></div>;
  return (
    <div className="crm-panel crm-book-scope">
      {booked && cal ? (
        <>
          <BookedPanel result={booked} calendar={{ ...cal, location_kind: cal.location_kind }} staff />
          <button type="button" className="button secondary small" onClick={() => onDone(done(booked))}>Done</button>
        </>
      ) : (
        <>
          {moving ? (
            <p className="crm-panel-note is-info">Moving the call on {whenLabel(moving.starts_at, "America/New_York")} ET. Pick the new time; the old one is cancelled when you confirm.</p>
          ) : others.length > 0 && (
            <p className="crm-panel-note">Already booked: {others.map((b) => `${whenLabel(b.starts_at, "America/New_York")} ET`).join(", ")}. To change that call, use Reschedule under Calls.</p>
          )}
          {open.length > 1 && (
            <label className="crm-panel-field">Calendar
              <select value={cal?.id} onChange={(e) => setCalId(Number(e.target.value))}>{open.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.duration_min} min)</option>)}</select>
            </label>
          )}
          {cal && (
            <BookingPicker
              key={cal.id}
              calendar={cal}
              slotsPath={`/api/crm/calendars/${cal.id}/preview${moving ? `?ignore=${moving.id}` : ""}`}
              onBook={(start) => api<BookedResult>(`/api/crm/contacts/${contactId}/book`, { method: "POST", body: JSON.stringify({ calendar_id: cal.id, start, replaces_booking_id: moving?.id }) })}
              onBooked={(result) => { setBooked(result); onBooked(); }}
            />
          )}
        </>
      )}
    </div>
  );
}

/** A YYYY-MM-DD day on the agency's (Eastern) calendar, `days` from now. */
const etDay = (days: number) => new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(Date.now() + days * 86400000));
const today = () => etDay(0);
const tomorrow = () => etDay(1);

/** A dated follow-up in the Work queue, linked back to this contact. */
function FollowUp({ contactId, ownerId, people, onDone }: { contactId: number; ownerId: number | null; people: Person[]; onDone: (msg: string) => void }) {
  const [due, setDue] = useState(tomorrow);
  const [who, setWho] = useState(ownerId ? String(ownerId) : "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api(`/api/crm/contacts/${contactId}/follow-up`, { method: "POST", body: JSON.stringify({ due_date: due, assigned_to: who || null, note }) });
      onDone("Follow-up added to the Work queue.");
    } catch (err) { setError(err instanceof Error ? err.message : "The follow-up was not saved."); }
    finally { setBusy(false); }
  }
  return (
    <form className="crm-panel crm-edit" onSubmit={save}>
      <div className="crm-edit-grid">
        <label>Day<input type="date" value={due} min={today()} onChange={(e) => setDue(e.target.value)} required /></label>
        <label>For
          <select value={who} onChange={(e) => setWho(e.target.value)}>
            <option value="">The contact's owner</option>
            {people.map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
          </select>
        </label>
        <label className="wide"><span>What to do <span className="crm-muted">Optional</span></span><input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="Send the concert case study" /></label>
      </div>
      {error && <p className="crm-error" role="alert">{error}</p>}
      <div className="crm-edit-actions"><button type="submit" className="button small" disabled={busy}>{busy ? "Adding…" : "Add follow-up"}</button></div>
    </form>
  );
}
